import { getFunctionsErrorCode } from "./callableErrors";

/**
 * In @firebase/functions v0.13.x a fetch-level failure (network drop, request
 * aborted by a hard navigation, blocked request) surfaces as
 * FunctionsError("internal") whose message is exactly the lowercase string
 * "internal" — the SDK never parsed a server response. A server unhandled
 * exception instead carries the uppercase "INTERNAL" message from the response
 * body. Version-coupled heuristic: if the SDK changes, the fail-safe direction
 * is that transport failures get reported at error level again.
 */
export function isTransientClaimError(error: unknown): boolean {
  const code = getFunctionsErrorCode(error);
  if (
    code === "functions/unavailable" ||
    code === "functions/deadline-exceeded"
  ) {
    return true;
  }
  if (code === "functions/internal") {
    return (error instanceof Error ? error.message : "") === "internal";
  }
  return false;
}

export const CLAIM_RETRY_DELAYS_MS = [500, 1500];

export interface TransientRetryDeps {
  isTransient?: (error: unknown) => boolean;
  /** Page-unload flag; when true, retrying a dead request is pointless. */
  isAborting?: () => boolean;
  delay?: (ms: number) => Promise<void>;
  delaysMs?: number[];
}

/**
 * Runs `call`, retrying transient failures with short backoff. Never retries
 * while the page is unloading — rethrows immediately so the caller decides
 * whether the failure is even worth reporting.
 */
export async function runWithTransientRetry<T>(
  call: () => Promise<T>,
  deps: TransientRetryDeps = {}
): Promise<T> {
  const {
    isTransient = isTransientClaimError,
    isAborting = () => false,
    delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    delaysMs = CLAIM_RETRY_DELAYS_MS,
  } = deps;

  for (let attempt = 0; ; attempt++) {
    try {
      return await call();
    } catch (error) {
      if (!isTransient(error) || attempt >= delaysMs.length || isAborting()) {
        throw error;
      }
      await delay(delaysMs[attempt]);
      if (isAborting()) {
        throw error;
      }
    }
  }
}
